import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Upload, Search, Download, X, CheckCircle2, Copy, Settings2, Archive, RotateCcw, Trash2 } from "lucide-react";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";
import { CHANNELS } from "@/lib/channels";
import PropertiesManager from "@/components/PropertiesManager";
import { useCompany } from "@/context/CompanyContext";

const money = (v) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", minimumFractionDigits: 2 }).format(v || 0);

function toCSV(rows) {
  const headers = [
    "Property", "Status", "Check-in", "Check-out", "Channel", "Booked By",
    "Guests", "Nights", "Nightly Rate", "Cleaning Fee", "Commission", "VAT",
    "Host Payout", "Security Deposit", "Payment Status", "Date Paid",
    "Reservation Code", "Company", "Contact", "Email",
  ];
  const esc = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v).replace(/"/g, '""');
    return /[",\n]/.test(s) ? `"${s}"` : s;
  };
  const lines = [headers.join(",")];
  for (const b of rows) {
    lines.push([
      b.property_booked, b.status, b.check_in, b.check_out, b.ota_or_direct, b.booked_by,
      b.guests, b.nights, b.nightly_rate, b.cleaning_fee, b.commission_fee, b.vat,
      b.host_payout, b.security_deposit, b.payment_status, b.date_paid,
      b.reservation_code, b.company_name, b.contact_no, b.email_address,
    ].map(esc).join(","));
  }
  return lines.join("\n");
}

const STATUS_OPTIONS = ["PRE-BOOKING", "ON-PROCESS", "UPCOMING", "CHECKED-IN", "CHECKED-OUT", "VACANT", "CANCELLED"];
const PAYMENT_OPTIONS = ["PAID", "PENDING", "REFUNDED", "NOT APPLICABLE"];

export default function Bookings() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [statusFilter, setStatusFilter] = useState("all");
  const [paymentFilter, setPaymentFilter] = useState("all");
  const [propertyFilter, setPropertyFilter] = useState("all");
  const [channelFilter, setChannelFilter] = useState("all");
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [propsManagerOpen, setPropsManagerOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const fileRef = useRef(null);
  const { company } = useCompany();
  const { user: authUser } = useAuth();
  const isAdmin = (authUser?.role || "ADMIN").toUpperCase() === "ADMIN";

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (statusFilter !== "all") params.status_filter = statusFilter;
      if (paymentFilter !== "all") params.payment_status = paymentFilter;
      if (propertyFilter !== "all") params.property_booked = propertyFilter;
      if (channelFilter !== "all") params.channel = channelFilter;
      params.archived = showArchived;
      params.limit = 2000;
      const r = await api.get("/bookings", { params });
      setRows(r.data);
      // Prune selected ids not in results
      setSelected((prev) => {
        const next = new Set();
        const ids = new Set(r.data.map((b) => b.id));
        prev.forEach((id) => ids.has(id) && next.add(id));
        return next;
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.get("/properties").then((r) => setProperties(r.data));
  }, []);

  // Keep q in sync with URL (?q=...) so global search bar can deep-link
  useEffect(() => {
    const urlQ = searchParams.get("q") || "";
    if (urlQ !== q) setQ(urlQ);
    // eslint-disable-next-line
  }, [searchParams]);

  useEffect(() => {
    const t = setTimeout(() => {
      fetchData();
      const cur = searchParams.get("q") || "";
      if (q && q !== cur) setSearchParams({ q }, { replace: true });
      else if (!q && cur) setSearchParams({}, { replace: true });
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q, statusFilter, paymentFilter, propertyFilter, channelFilter, showArchived, company]);

  const onImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    toast.promise(
      api.post("/bookings/import", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => {
        fetchData();
        return `Imported ${r.data.imported} bookings`;
      }),
      { loading: "Importing…", success: (m) => m, error: "Import failed" }
    );
    e.target.value = "";
  };

  const exportRows = selected.size > 0 ? rows.filter((r) => selected.has(r.id)) : rows;
  const onExport = () => {
    if (!exportRows.length) {
      toast.info("No rows to export");
      return;
    }
    const csv = toCSV(exportRows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `irentals-bookings-${new Date().toISOString().slice(0,10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${exportRows.length} rows`);
  };

  const toggleRow = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allChecked = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const someChecked = selected.size > 0 && !allChecked;
  const toggleAll = () => {
    if (allChecked) setSelected(new Set());
    else setSelected(new Set(rows.map((r) => r.id)));
  };
  const clearSelection = () => setSelected(new Set());

  const applyBulk = async (field, value) => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    const payload = { ids };
    payload[field] = value;
    try {
      const res = await api.post("/bookings/bulk_update", payload);
      toast.success(`Updated ${res.data.modified} booking${res.data.modified === 1 ? "" : "s"}`);
      clearSelection();
      fetchData();
    } catch (e) {
      toast.error("Bulk update failed");
    }
  };

  const runDedupe = async () => {
    try {
      const dry = await api.post("/bookings/dedupe?dry_run=true");
      const dupCount = dry.data.would_remove || 0;
      if (dupCount === 0) {
        toast.success("No duplicate bookings found");
        return;
      }
      if (!confirm(`Found ${dupCount} duplicate booking${dupCount === 1 ? "" : "s"} to remove. Continue?`)) return;
      const res = await api.post("/bookings/dedupe");
      toast.success(`Removed ${res.data.removed} duplicates`);
      fetchData();
    } catch (e) {
      toast.error("Dedupe failed");
    }
  };

  const archiveSelected = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    if (!confirm(`Archive ${ids.length} booking${ids.length === 1 ? "" : "s"}? They can be restored from the Archive view.`)) return;
    try {
      const r = await api.post("/bookings/archive_bulk", { ids });
      toast.success(`Archived ${r.data.archived} bookings`);
      clearSelection();
      fetchData();
    } catch {
      toast.error("Archive failed");
    }
  };

  const restoreSelected = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    for (const id of ids) await api.post(`/bookings/${id}/restore`);
    toast.success(`Restored ${ids.length} bookings`);
    clearSelection();
    fetchData();
  };

  const deleteSelected = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    if (!confirm(`Permanently delete ${ids.length} booking${ids.length === 1 ? "" : "s"}? This cannot be undone.`)) return;
    for (const id of ids) await api.delete(`/bookings/${id}`);
    toast.success(`Deleted ${ids.length} bookings`);
    clearSelection();
    fetchData();
  };

  const purgeArchive = async () => {
    if (!confirm("Permanently delete ALL archived bookings? This cannot be undone.")) return;
    const r = await api.post("/bookings/purge_archived");
    toast.success(`Purged ${r.data.deleted} bookings`);
    fetchData();
  };

  return (
    <div className="p-4 sm:p-6 lg:p-10 space-y-6 max-w-[1600px]">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow">Reservations</div>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight mt-2 text-slate-900">
            Bookings
          </h1>
          <p className="text-sm text-slate-500 mt-2">
            {rows.length} record{rows.length !== 1 ? "s" : ""} · Full ledger of every stay.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <input ref={fileRef} type="file" accept=".xlsx,.xls" hidden onChange={onImport} data-testid="import-file-input" />
          {isAdmin && (
            <Button
              variant="outline"
              onClick={() => setShowArchived((v) => !v)}
              className={`rounded-sm border-slate-300 transition-colors ${showArchived ? "bg-blue-900 text-white border-blue-900" : "text-slate-700 hover:bg-slate-100"}`}
              data-testid="toggle-archived"
            >
              <Archive className="w-4 h-4 mr-2" /> {showArchived ? "Viewing Archive" : "Archive"}
            </Button>
          )}
          {showArchived && isAdmin && (
            <Button
              variant="outline"
              onClick={purgeArchive}
              className="rounded-sm border-rose-300 text-rose-700 hover:bg-rose-50"
              data-testid="purge-archive"
            >
              <Trash2 className="w-4 h-4 mr-2" /> Empty archive
            </Button>
          )}
          {isAdmin && (
            <Button
              variant="outline"
              onClick={() => setPropsManagerOpen(true)}
              className="rounded-sm border-slate-300 text-slate-700 hover:bg-slate-100 transition-colors"
              data-testid="manage-properties-button"
            >
              <Settings2 className="w-4 h-4 mr-2" /> Properties
            </Button>
          )}
          {isAdmin && (
            <Button
              variant="outline"
              onClick={onExport}
              className="rounded-sm border-slate-300 text-slate-700 hover:bg-slate-100 transition-colors"
              data-testid="export-csv-button"
            >
            <Download className="w-4 h-4 mr-2" />
            {selected.size > 0 ? `Export ${selected.size} selected` : "Export CSV"}
          </Button>
          )}
          {isAdmin && (
          <Button
            variant="outline"
            onClick={() => fileRef.current?.click()}
            className="rounded-sm border-slate-300 text-slate-700 hover:bg-blue-900 hover:text-white hover:border-blue-900 transition-colors"
            data-testid="import-excel-button"
          >
            <Upload className="w-4 h-4 mr-2" /> Import Excel
          </Button>
          )}
          {isAdmin && (
          <Button
            onClick={() => navigate("/bookings/new")}
            className="rounded-sm bg-blue-900 hover:bg-blue-800 text-white active:scale-[0.98]"
            data-testid="add-booking-button"
          >
            <Plus className="w-4 h-4 mr-2" /> Add Booking
          </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-sm p-4 grid grid-cols-1 md:grid-cols-5 gap-3">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search code, guest, email…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 rounded-sm border-slate-300 h-10 focus:border-blue-900 focus:ring-blue-900"
            data-testid="search-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="rounded-sm border-slate-300 h-10" data-testid="status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={channelFilter} onValueChange={setChannelFilter}>
          <SelectTrigger className="rounded-sm border-slate-300 h-10" data-testid="channel-filter"><SelectValue placeholder="Channel" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All channels</SelectItem>
            {CHANNELS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={paymentFilter} onValueChange={setPaymentFilter}>
          <SelectTrigger className="rounded-sm border-slate-300 h-10" data-testid="payment-filter"><SelectValue placeholder="Payment" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All payments</SelectItem>
            {PAYMENT_OPTIONS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={propertyFilter} onValueChange={setPropertyFilter}>
          <SelectTrigger className="rounded-sm border-slate-300 h-10" data-testid="property-filter"><SelectValue placeholder="Property" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All properties</SelectItem>
            {properties.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Bulk action bar (only when at least one selected) */}
      {selected.size > 0 && (
        <div
          className="bg-blue-900 text-white rounded-sm p-3 sm:p-4 flex flex-wrap items-center gap-3 sticky top-0 lg:top-0 z-20 shadow-sm"
          data-testid="bulk-action-bar"
        >
          <div className="flex items-center gap-2 mr-auto">
            <CheckCircle2 className="w-5 h-5" />
            <span className="text-sm font-semibold" data-testid="bulk-count">
              {selected.size} selected
            </span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {!showArchived ? (
              <>
                <span className="text-xs uppercase tracking-wider text-blue-200">Set status</span>
                <Select onValueChange={(v) => applyBulk("status", v)}>
                  <SelectTrigger className="bg-white text-slate-900 rounded-sm h-9 w-40" data-testid="bulk-status-select">
                    <SelectValue placeholder="Choose…" />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((s) => (
                      <SelectItem key={s} value={s} data-testid={`bulk-status-${s}`}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="text-xs uppercase tracking-wider text-blue-200">Payment</span>
                <Select onValueChange={(v) => applyBulk("payment_status", v)}>
                  <SelectTrigger className="bg-white text-slate-900 rounded-sm h-9 w-40" data-testid="bulk-payment-select">
                    <SelectValue placeholder="Choose…" />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_OPTIONS.map((p) => (
                      <SelectItem key={p} value={p} data-testid={`bulk-payment-${p}`}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="text-xs uppercase tracking-wider text-blue-200">Channel</span>
                <Select onValueChange={(v) => applyBulk("ota_or_direct", v)}>
                  <SelectTrigger className="bg-white text-slate-900 rounded-sm h-9 w-40" data-testid="bulk-channel-select">
                    <SelectValue placeholder="Choose…" />
                  </SelectTrigger>
                  <SelectContent>
                    {CHANNELS.map((c) => (
                      <SelectItem key={c} value={c} data-testid={`bulk-channel-${c}`}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  onClick={archiveSelected}
                  className="rounded-sm border-amber-300 bg-transparent text-white hover:bg-amber-700 hover:text-white h-9"
                  data-testid="bulk-archive"
                  disabled={!isAdmin}
                >
                  <Archive className="w-4 h-4 mr-1.5" /> Archive
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={restoreSelected}
                  className="rounded-sm border-emerald-300 bg-transparent text-white hover:bg-emerald-700 hover:text-white h-9"
                  data-testid="bulk-restore"
                  disabled={!isAdmin}
                >
                  <RotateCcw className="w-4 h-4 mr-1.5" /> Restore
                </Button>
                <Button
                  variant="outline"
                  onClick={deleteSelected}
                  className="rounded-sm border-rose-300 bg-transparent text-white hover:bg-rose-700 hover:text-white h-9"
                  data-testid="bulk-delete"
                  disabled={!isAdmin}
                >
                  <Trash2 className="w-4 h-4 mr-1.5" /> Delete forever
                </Button>
              </>
            )}
            <Button
              variant="outline"
              onClick={clearSelection}
              className="rounded-sm border-blue-300 bg-transparent text-white hover:bg-blue-800 hover:text-white h-9"
              data-testid="bulk-clear"
            >
              <X className="w-4 h-4 mr-1.5" /> Unselect
            </Button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="table-scroll overflow-x-auto">
          <table className="w-full text-sm min-w-[1750px]">
            <thead className="bg-slate-100 border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-600">
              <tr>
                <th className="px-3 py-3 w-10 sticky left-0 bg-slate-100 z-10 border-r border-slate-200">
                  <Checkbox
                    checked={allChecked}
                    onCheckedChange={toggleAll}
                    aria-label="Select all"
                    data-testid="select-all-checkbox"
                    className={someChecked ? "data-[state=checked]:bg-blue-900" : ""}
                  />
                </th>
                <th className="text-left px-3 py-3 font-semibold min-w-[160px]">Property</th>
                <th className="text-left px-3 py-3 font-semibold">Status</th>
                <th className="text-left px-3 py-3 font-semibold">Check-in</th>
                <th className="text-left px-3 py-3 font-semibold">Check-out</th>
                <th className="text-left px-3 py-3 font-semibold">Channel</th>
                <th className="text-left px-3 py-3 font-semibold">Booked By</th>
                <th className="text-right px-3 py-3 font-semibold">Guests</th>
                <th className="text-right px-3 py-3 font-semibold">Nights</th>
                <th className="text-right px-3 py-3 font-semibold">Rate</th>
                <th className="text-right px-3 py-3 font-semibold">Cleaning</th>
                <th className="text-right px-3 py-3 font-semibold">Commission</th>
                <th className="text-right px-3 py-3 font-semibold">VAT</th>
                <th className="text-right px-3 py-3 font-semibold">Payout</th>
                <th className="text-right px-3 py-3 font-semibold">Deposit</th>
                <th className="text-left px-3 py-3 font-semibold">Payment</th>
                <th className="text-left px-3 py-3 font-semibold">Reservation</th>
                <th className="text-left px-3 py-3 font-semibold">Company</th>
                <th className="text-left px-3 py-3 font-semibold">Contact</th>
                <th className="text-left px-3 py-3 font-semibold">Email</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={20} className="p-6 text-center text-slate-500">Loading…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={20} className="p-8 text-center text-slate-500">No bookings match your filters.</td></tr>
              )}
              {rows.map((b) => {
                const isSelected = selected.has(b.id);
                return (
                  <tr
                    key={b.id}
                    className={`border-b border-slate-100 cursor-pointer transition-colors ${
                      isSelected ? "bg-blue-50" : "hover:bg-blue-50/40"
                    }`}
                    onClick={() => navigate(`/bookings/${b.id}`)}
                    data-testid={`booking-row-${b.id}`}
                  >
                    <td
                      className="px-3 py-3 sticky left-0 z-10 border-r border-slate-100 bg-inherit"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => toggleRow(b.id)}
                        aria-label={`Select ${b.reservation_code || b.id}`}
                        data-testid={`row-check-${b.id}`}
                      />
                    </td>
                    <td className="px-3 py-3 font-medium">{b.property_booked || "—"}</td>
                    <td className="px-3 py-3"><StatusBadge value={b.status} /></td>
                    <td className="px-3 py-3">{b.check_in || "—"}</td>
                    <td className="px-3 py-3">{b.check_out || "—"}</td>
                    <td className="px-3 py-3"><StatusBadge value={b.ota_or_direct} type="channel" /></td>
                    <td className="px-3 py-3">{b.booked_by || "—"}</td>
                    <td className="px-3 py-3 text-right tabular-nums">{b.guests || 0}</td>
                    <td className="px-3 py-3 text-right tabular-nums">{b.nights || 0}</td>
                    <td className="px-3 py-3 text-right tabular-nums">{money(b.nightly_rate)}</td>
                    <td className="px-3 py-3 text-right tabular-nums">{money(b.cleaning_fee)}</td>
                    <td className="px-3 py-3 text-right tabular-nums">{money(b.commission_fee)}</td>
                    <td className="px-3 py-3 text-right tabular-nums">{money(b.vat)}</td>
                    <td className="px-3 py-3 text-right tabular-nums font-semibold text-blue-900">{money(b.host_payout)}</td>
                    <td className="px-3 py-3 text-right tabular-nums">{money(b.security_deposit)}</td>
                    <td className="px-3 py-3"><StatusBadge value={b.payment_status} type="payment" /></td>
                    <td className="px-3 py-3 font-mono text-xs">{b.reservation_code || "—"}</td>
                    <td className="px-3 py-3">{b.company_name || "—"}</td>
                    <td className="px-3 py-3">{b.contact_no || "—"}</td>
                    <td className="px-3 py-3 truncate max-w-[220px]">{b.email_address || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <PropertiesManager
        open={propsManagerOpen}
        onOpenChange={setPropsManagerOpen}
        onChanged={() => {
          api.get("/properties").then((r) => setProperties(r.data));
          fetchData();
        }}
      />
    </div>
  );
}
